import { useEffect, useMemo, useRef, useState } from "react";
import { LuCheck, LuCircleHelp, LuLightbulb, LuLock, LuSparkles } from "react-icons/lu";
import { FeedbackBox, QuestionCard, revealFor, type QView } from "../components/QuestionCard";
import { banner, pop, useLogEvents } from "../components/juice";
import { buzz, play } from "../sound";
import type { GameProps } from "../pages/Match";
import { MEMORY_SYMBOLS } from "../../shared/games/videnspar";
import { GLYPHS } from "./glyphs";

/**
 * The single-player puzzles (Sudoku, Videnspar). They share one frame: the puzzle on the left, and on the right
 * the question the puzzle is asking for (always the same card as everywhere else on EduBattle), the status and a few numbers.
 */
function PuzzleFrame({ p, status, tone, motto, stats, children }: { p: GameProps; status: string; tone?: "good" | "warn"; motto: string; stats: [string, string | number][]; children: any }) {
  const st = p.view.state;
  const q: QView | null = st.question;
  const [recent, setRecent] = useState<{ q: QView; given: number | string } | null>(null);
  const qRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!q) return;
    p.clearFeedback();
    /* on a phone the question appears under the puzzle – bring it into view */
    if (typeof window !== "undefined" && window.innerWidth < 900) qRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    // eslint-disable-next-line
  }, [q?.pid]);
  const onAnswer = (given: number | string) => { if (q) setRecent({ q, given }); return p.answer(given); };
  const me = st.stats?.[0];
  return (
    <div className="arena-grid wide pz">
      <div className="arena-center">
        {children}
        <div className="motto">{motto}</div>
      </div>
      <div className="sidecol r">
        <div className={`notice ${tone ?? ""}`} role="status" aria-live="polite"><strong>{status}</strong></div>
        <div ref={qRef}>
          {q ? <QuestionCard q={q} serverNow={st.now} onAnswer={onAnswer} busy={p.busy} />
            : recent && p.feedback ? <QuestionCard q={recent.q} reveal={revealFor(recent.q, recent.given, p.feedback)} onAnswer={() => undefined} />
            : p.feedback ? <FeedbackBox fb={p.feedback} /> : null}
        </div>
        <section className="card" aria-label="Status">
          <div className="panel-title">Dit spil</div>
          {stats.map(([k, v]) => <div key={k} className="statline"><span>{k}</span><b>{v}</b></div>)}
          <div className="statline"><span>Rigtige svar</span><b>{me?.correct ?? 0} / {me?.answered ?? 0}</b></div>
          <div className="statline"><span>Point lige nu</span><b>{st.liveScore ?? 0}</b></div>
        </section>
      </div>
    </div>
  );
}

/* ═════════════════════════════ Sudoku ═════════════════════════════ */
export function Sudoku(p: GameProps) {
  const st = p.view.state;
  const b = st.board;
  const asking = !!st.question;
  const [sel, setSel] = useState<number | null>(null);
  const [shake, setShake] = useState<number | null>(null);
  const [fresh, setFresh] = useState<number | null>(null);
  const [note, setNote] = useState("");
  const n: number = b.n;
  const boxOf = (i: number) => Math.floor(Math.floor(i / n) / b.br) * (n / b.bc) + Math.floor((i % n) / b.bc);
  const boxesAcross = n / b.bc;
  const locked = (i: number) => !b.open[boxOf(i)];
  const idle = !asking && !p.busy && b.phase === "play";

  useLogEvents(st.log, p.view.id, (e) => {
    if (e.kind === "place" || e.kind === "hint") {
      setFresh(e.data?.cell ?? null);
      play("place");
      buzz(10);
      if (e.kind === "hint") pop("Hint!", "blue");
      setNote("");
    } else if (e.kind === "slip") {
      setShake(e.data?.cell ?? null);
      play("slip");
      setNote(`Næsten – ${e.data?.digit} passer ikke dér. Prøv et andet tal!`);
      setTimeout(() => setShake(null), 520);
    } else if (e.kind === "unlock") { play("unlock"); banner("Boksen er åben!", "gold"); buzz([20, 30, 40]); setNote(""); }
    else if (e.kind === "locked") pop("Prøv et nyt spørgsmål", "soft");
    else if (e.kind === "no-hint") pop("Intet hint denne gang", "soft");
    else if (e.kind === "box-done") { play("pair"); pop("Boks klaret!", "good"); }
  });

  const unlock = (box: number) => { if (idle) void p.act({ type: "unlock", box }); };
  const put = (d: number) => {
    if (!idle) return;
    if (sel === null) { setNote("Tryk først på et tomt felt."); return; }
    if (b.given[sel] || b.cells[sel]) { setNote("Det felt er allerede udfyldt – vælg et tomt."); return; }
    if (locked(sel)) { unlock(boxOf(sel)); return; }
    void p.act({ type: "place", cell: sel, digit: d });
  };
  const hint = () => { if (idle) void p.act({ type: "hint" }); };

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (asking || (e.target as HTMLElement)?.matches?.("input,textarea,select")) return;
      if (/^[1-9]$/.test(e.key) && Number(e.key) <= n) { put(Number(e.key)); return; }
      const dir: Record<string, [number, number]> = { ArrowUp: [0, -1], ArrowDown: [0, 1], ArrowLeft: [-1, 0], ArrowRight: [1, 0] };
      const d = dir[e.key];
      if (d && sel !== null) {
        e.preventDefault();
        const x = Math.max(0, Math.min(n - 1, (sel % n) + d[0])), y = Math.max(0, Math.min(n - 1, Math.floor(sel / n) + d[1]));
        setSel(y * n + x);
      }
    };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  });

  const counts = useMemo(() => {
    const c = new Array(n + 1).fill(0);
    for (const v of b.cells as number[]) if (v) c[v]++;
    return c;
  }, [b.cells, n]);
  const selVal = sel !== null ? b.cells[sel] : 0;
  const boxesLeft: number = b.boxesLeft;
  const status = b.phase === "over" ? "Sudokuen er løst – flot!"
    : asking ? (st.question?.purpose === "hint" ? "Svar rigtigt – så får du et hint." : "Svar rigtigt – så åbner boksen!")
    : boxesLeft > 0 ? "Tryk på en tåget boks for at åbne den med viden. Skriv i de åbne bokse." : "Alle bokse er åbne – gør den færdig!";
  return (
    <PuzzleFrame p={p} status={status} tone={asking ? "warn" : "good"} motto="Åbn boksene · Find tallene · Løs sudokuen" stats={[["Bokse åbnet", `${n - boxesLeft} / ${n}`], ["Felter tilbage", b.empty], ["Fejlskud", b.slips], ["Hints", b.hints]]}>
      <div className="sdk-wrap">
        <div className="sdk" style={{ ["--n" as any]: n }}>
          <div className="sdk-grid" role="grid" aria-label={`Sudoku ${n} gange ${n}`}>
            {(b.cells as number[]).map((v, i) => {
              const r = Math.floor(i / n), c = i % n;
              const peer = sel !== null && (Math.floor(sel / n) === r || sel % n === c || boxOf(sel) === boxOf(i));
              const cls = [
                "sdk-cell", b.given[i] ? "given" : v ? "filled" : "empty", sel === i ? "sel" : "", peer ? "peer" : "", selVal && v === selVal ? "same" : "",
                locked(i) ? "locked" : "", (c + 1) % b.bc === 0 && c < n - 1 ? "bR" : "", (r + 1) % b.br === 0 && r < n - 1 ? "bB" : "",
                fresh === i ? "fresh" : "", shake === i ? "shake" : "",
              ].join(" ");
              return (
                <button key={i} type="button" role="gridcell" className={cls} onClick={() => setSel(i)} aria-selected={sel === i}
                  aria-label={`Række ${r + 1}, kolonne ${c + 1}: ${v ? v : "tom"}${locked(i) ? ", i en låst boks" : ""}`}>{v || ""}</button>
              );
            })}
            {(b.open as boolean[]).map((o, bi) => {
              if (o) return null;
              const row = Math.floor(bi / boxesAcross), col = bi % boxesAcross;
              return (
                <button key={`f${bi}`} type="button" className={`sdk-fog ${b.target === bi ? "asking" : ""}`} disabled={!idle} onClick={() => unlock(bi)}
                  style={{ left: `${(col * b.bc / n) * 100}%`, top: `${(row * b.br / n) * 100}%`, width: `${(b.bc / n) * 100}%`, height: `${(b.br / n) * 100}%` }}
                  aria-label={`Åbn boks ${bi + 1} ved at svare på et spørgsmål`}>
                  <span className="fog-chip"><LuLock size={15} aria-hidden="true" /><b>Åbn</b></span>
                </button>
              );
            })}
          </div>
        </div>
        <div className="sdk-pad" role="group" aria-label="Vælg et tal">
          {Array.from({ length: n }, (_, k) => k + 1).map((d) => (
            <button key={d} type="button" className="btn ghost sdk-key" disabled={!idle || counts[d]! >= n} onClick={() => put(d)} aria-label={`Skriv ${d}`}>
              {d}<small>{n - counts[d]!}</small>
            </button>
          ))}
          <button type="button" className="btn violet sdk-hint" disabled={!idle || b.empty === 0} onClick={hint}><LuLightbulb size={16} aria-hidden="true" /> Hint</button>
        </div>
        <p className="sdk-note" role="status" aria-live="polite">{note || "Tryk på et felt og vælg et tal. Hvert tal må kun stå én gang i hver række, kolonne og boks."}</p>
      </div>
    </PuzzleFrame>
  );
}

/* ═════════════════════════════ Videnspar ═════════════════════════════ */
export function Videnspar(p: GameProps) {
  const st = p.view.state;
  const b = st.board;
  const asking = !!st.question;
  const act = p.act;
  const actRef = useRef(act);
  actRef.current = act;
  const [pulse, setPulse] = useState<number[]>([]);
  useLogEvents(st.log, p.view.id, (e) => {
    if (e.kind === "flip") { play("flip"); buzz(8); }
    else if (e.kind === "miss") { play("slip"); }
    else if (e.kind === "pair") { play("pair"); pop((e.data?.streak ?? 0) >= 2 ? `${e.data?.streak} par i træk!` : "Par!", "gold"); buzz([20, 30, 40]); setPulse([e.data?.a, e.data?.b]); setTimeout(() => setPulse([]), 900); }
    else if (e.kind === "again") pop("Prøv igen – nu ved du, hvor de ligger!", "soft");
  });
  /* two different cards turn back over by themselves after a moment */
  useEffect(() => {
    if (b.mode !== "miss") return;
    const t = setTimeout(() => { void actRef.current({ type: "hide" }); }, calmDelay());
    return () => clearTimeout(t);
  }, [b.mode, b.flips]);
  const idle = !asking && !p.busy && b.phase === "play" && b.mode !== "ask";
  const status = b.phase === "over" ? "Alle par er fundet – flot husket!"
    : b.mode === "ask" ? "Et par! Svar rigtigt for at beholde det."
    : b.mode === "miss" ? "Ikke et par – husk, hvor de lå."
    : "Vend to kort og find dem, der er ens.";
  return (
    <PuzzleFrame p={p} status={status} tone={b.mode === "ask" ? "warn" : undefined} motto="Husk kortene · Find parrene · Behold dem med viden" stats={[["Par fundet", `${b.claimed} / ${b.pairs}`], ["Streg i træk", b.streak], ["Forkerte vendinger", b.misses]]}>
      <div className="mem-wrap">
        <div className="mem" style={{ ["--cols" as any]: b.cols, ["--rows" as any]: Math.ceil(b.cards.length / b.cols) }} role="grid" aria-label="Videnspar">
          {(b.cards as (number | null)[]).map((f, i) => {
            const up = f !== null;
            const sym = f !== null ? MEMORY_SYMBOLS[f]! : null;
            const I = sym ? GLYPHS[sym.icon] : null;
            const cls = `mcard ${up ? "up" : ""} ${b.matched[i] ? "matched" : ""} ${pulse.includes(i) ? "pulse" : ""} ${b.mode === "ask" && b.up.includes(i) ? "asking" : ""}`;
            return (
              <button key={i} type="button" role="gridcell" className={cls} disabled={!idle || up} onClick={() => p.act({ type: "flip", i })}
                aria-label={up ? `Kort ${i + 1}: ${sym!.icon.replace("Lu", "")}${b.matched[i] ? ", fundet" : ""}` : `Kort ${i + 1}, vendt om`}>
                <span className="mc-in">
                  <span className="mc-back" aria-hidden="true"><LuCircleHelp size="46%" strokeWidth={2.4} /></span>
                  <span className="mc-front" style={{ color: sym?.color }} aria-hidden="true">{I && <I size="62%" strokeWidth={2.2} />}{b.matched[i] && <i className="mc-ok"><LuCheck size={14} /></i>}</span>
                </span>
              </button>
            );
          })}
        </div>
        <p className="sdk-note" role="status">Finder du to ens kort, får du et spørgsmål. Rigtigt svar = du beholder parret. Forkert svar = kortene vender om igen.</p>
      </div>
    </PuzzleFrame>
  );
}
const calmDelay = () => (typeof document !== "undefined" && document.documentElement.classList.contains("reduce-motion") ? 1600 : 1000);
